import React from "react";
import { Pause, Play, X, RotateCcw, Loader2, CheckCircle2, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

const STATUS = {
  running: { c: "#00F0FF", icon: Loader2, spin: true, label: "Running" },
  queued: { c: "#E6B04E", icon: Clock, label: "Queued" },
  paused: { c: "#94A3B8", icon: Pause, label: "Paused" },
  completed: { c: "#10B981", icon: CheckCircle2, label: "Completed" },
  failed: { c: "#EF4444", icon: AlertTriangle, label: "Failed" },
  cancelled: { c: "#64748B", icon: X, label: "Cancelled" },
};

export default function Jobs() {
  const { projectId } = useApp();
  const [tab, setTab] = React.useState("ALL");
  const { data, refresh } = usePoll(
    () => (projectId ? api.listJobs(projectId, { status: tab, limit: 200 }) : Promise.resolve([])),
    1800, [projectId, tab]
  );
  const { data: summary } = usePoll(
    () => (projectId ? api.jobsSummary(projectId) : Promise.resolve({})), 1800, [projectId]
  );

  if (!projectId) return <NoProject title="Job queue needs a project" />;
  const jobs = data || [];

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error("Action failed"); } };

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Background Job Queue</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Persistent &amp; resumable — survives restarts and crashes</p>
        </div>
        <button data-testid="btn-retry-failed" className="btn btn-ghost" onClick={() => act(() => api.retryFailed(projectId), "Failed jobs requeued")}>
          <RefreshCw size={15} /> Retry All Failed
        </button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {["ALL", "running", "queued", "paused", "completed", "failed", "cancelled"].map((s) => (
          <button key={s} data-testid={`job-filter-${s}`} onClick={() => setTab(s)}
            className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${tab === s ? "bg-[#212534] text-[#E6B04E]" : "text-[#94A3B8] hover:bg-[#181B26]"}`}>
            {s === "ALL" ? "All" : STATUS[s].label}
            {summary && s !== "ALL" && <span className="ml-1.5 font-mono text-[11px] text-[#64748B]">{summary[s] ?? 0}</span>}
          </button>
        ))}
      </div>

      <div className="studio-card divide-y divide-[#252A3B]">
        {jobs.length === 0 && <div className="p-10 text-center text-[#64748B]">No jobs in this view.</div>}
        {jobs.map((j) => {
          const st = STATUS[j.status] || STATUS.queued;
          const Icon = st.icon;
          return (
            <div key={j.id} data-testid={`job-row-${j.id}`} className="p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${st.c}1a` }}>
                <Icon size={16} style={{ color: st.c }} className={st.spin ? "animate-spin" : ""} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium truncate">{j.label}</span>
                  <span className="tech-badge">{j.type}</span>
                </div>
                {j.status === "running" || (j.progress > 0 && j.progress < 100) ? (
                  <div className="progress-track mt-2 max-w-md"><div className="progress-fill" style={{ width: `${j.progress}%` }} /></div>
                ) : null}
                {j.error && <div className="text-[11px] font-mono text-[#f87171] mt-1 truncate">{j.error}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-[12px] w-10 text-right" style={{ color: st.c }}>{j.progress}%</span>
                {j.status === "queued" && (
                  <button data-testid={`btn-pause-${j.id}`} title="Pause" className="p-1.5 rounded hover:bg-[#212534] text-[#94A3B8]" onClick={() => act(() => api.pauseJob(j.id), "Paused")}><Pause size={15} /></button>
                )}
                {(j.status === "paused" || j.status === "failed" || j.status === "cancelled") && (
                  <button data-testid={`btn-resume-${j.id}`} title="Resume" className="p-1.5 rounded hover:bg-[#212534] text-[#10B981]" onClick={() => act(() => api.resumeJob(j.id), "Resumed")}><Play size={15} /></button>
                )}
                {j.status === "failed" && (
                  <button data-testid={`btn-retry-${j.id}`} title="Retry" className="p-1.5 rounded hover:bg-[#212534] text-[#00F0FF]" onClick={() => act(() => api.retryJob(j.id), "Requeued")}><RotateCcw size={15} /></button>
                )}
                {(j.status === "queued" || j.status === "running" || j.status === "paused") && (
                  <button data-testid={`btn-cancel-${j.id}`} title="Cancel" className="p-1.5 rounded hover:bg-[#212534] text-[#EF4444]" onClick={() => act(() => api.cancelJob(j.id), "Cancelling")}><X size={15} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
